import React from "react";
import { VisuallyHidden } from "react-aria-components";
import { t } from "ttag";

import { MediaChooserBlockOrNull, ThemeChooserBlockOrNull } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable, IconButton } from "../Clickables";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { getThemeClass } from "../ThemedModule";
import { VideoPlayerControls } from "../VideoPlayerControls";
import { GlobeSvg } from "./GlobeSvg";

import styles from "./SplitCarousel.module.scss";

const SNAP_THRESHOLD = 0.15;
const MEDIA_DURATION_MS = 800;
const MEDIA_TRANSITION = `transform ${MEDIA_DURATION_MS}ms ease-in-out`;

interface SplitCarouselSlide {
    media: MediaChooserBlockOrNull;
}

interface SplitCarouselProps<T extends SplitCarouselSlide> {
    slides: T[];
    showGraphic?: boolean | null;
    moduleTheme?: ThemeChooserBlockOrNull;
    cardTheme?: ThemeChooserBlockOrNull;
    ariaLabel: string;
    renderTextPanel: (slide: T, index: number) => React.ReactNode;
    textPanelClassName?: string;
}

export const SplitCarousel = <T extends SplitCarouselSlide>({
    slides,
    showGraphic,
    moduleTheme,
    cardTheme,
    ariaLabel,
    renderTextPanel,
    textPanelClassName,
}: SplitCarouselProps<T>) => {
    const slideCount = slides.length;

    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [dragProgress, setDragProgress] = React.useState(0);
    const [isSnapping, setIsSnapping] = React.useState(false);
    const [snapTarget, setSnapTarget] = React.useState(0);
    const [activeVideo, setActiveVideo] =
        React.useState<HTMLVideoElement | null>(null);

    const rootRef = React.useRef<HTMLDivElement>(null);
    const centerMediaRef = React.useRef<HTMLDivElement>(null);
    const dragState = React.useRef({ isDragging: false, startX: 0 });
    const dragProgressRef = React.useRef(0);
    const snapInProgress = React.useRef(false);

    const wrap = (i: number) =>
        slideCount === 0 ? 0 : ((i % slideCount) + slideCount) % slideCount;

    // Two clones on each side so side cards always have content during wrap.
    const fullSlides = React.useMemo(() => {
        if (slideCount === 0) return [];
        const w = (i: number) => ((i % slideCount) + slideCount) % slideCount;
        return [
            slides[w(-2)],
            slides[w(-1)],
            ...slides,
            slides[w(slideCount)],
            slides[w(slideCount + 1)],
        ];
    }, [slides, slideCount]);

    // Filmstrip layout: each slide is placed at its position in the strip
    // (positionOffset * 100%) and the whole strip translates by dragOrSnap.
    // Adjacent slides ride alongside the home slide instead of stacking on top.
    const getStackSlideStyle = (
        slideIdx: number,
        offset: number,
    ): React.CSSProperties => {
        const dragOrSnap = isSnapping ? snapTarget : dragProgress;
        const homeIdx = currentIndex + 2 + offset;
        const positionOffset = slideIdx - homeIdx;

        // Slides further than one position away stay parked off-screen with
        // no transition — they only matter after a currentIndex shift.
        if (Math.abs(positionOffset) > 1) {
            return {
                transform: `translateX(${positionOffset * 100}%)`,
                transition: "none",
            };
        }

        return {
            transform: `translateX(${(positionOffset + dragOrSnap) * 100}%)`,
            transition: isSnapping ? MEDIA_TRANSITION : "none",
        };
    };

    const getActiveVideo = (): HTMLVideoElement | null => {
        return (
            centerMediaRef.current?.querySelector<HTMLVideoElement>(
                "[data-active-slide] video",
            ) ?? null
        );
    };

    const resetActiveVideo = () => {
        const video = getActiveVideo();
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
    };

    const goNext = () => {
        if (isSnapping || slideCount < 2) return;
        resetActiveVideo();
        snapInProgress.current = true;
        setSnapTarget(-1);
        setIsSnapping(true);
    };

    const goPrev = () => {
        if (isSnapping || slideCount < 2) return;
        resetActiveVideo();
        snapInProgress.current = true;
        setSnapTarget(1);
        setIsSnapping(true);
    };

    const goTo = (index: number) => {
        if (isSnapping || index === currentIndex) return;
        resetActiveVideo();
        setCurrentIndex(wrap(index));
        dragProgressRef.current = 0;
        setDragProgress(0);
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (isSnapping || slideCount < 2) return;
        dragState.current = { isDragging: true, startX: e.clientX };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.setAttribute("data-dragging", "");
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragState.current.isDragging) return;
        const delta = e.clientX - dragState.current.startX;
        // Measure against the media viewport so the image follows the finger
        // 1:1 across breakpoints (center card layout differs on mobile).
        const width = centerMediaRef.current?.offsetWidth ?? 600;
        const progress = Math.max(-1, Math.min(1, delta / width));
        dragProgressRef.current = progress;
        setDragProgress(progress);
    };

    const handlePointerUp = () => {
        if (!dragState.current.isDragging) return;
        dragState.current.isDragging = false;
        centerMediaRef.current?.removeAttribute("data-dragging");
        const progress = dragProgressRef.current;

        if (progress < -SNAP_THRESHOLD) {
            resetActiveVideo();
            snapInProgress.current = true;
            setSnapTarget(-1);
            setIsSnapping(true);
        } else if (progress > SNAP_THRESHOLD) {
            resetActiveVideo();
            snapInProgress.current = true;
            setSnapTarget(1);
            setIsSnapping(true);
        } else if (Math.abs(progress) > 0.01) {
            snapInProgress.current = true;
            setSnapTarget(0);
            setIsSnapping(true);
        } else {
            setDragProgress(0);
        }
    };

    const finishSnap = React.useCallback(() => {
        if (!snapInProgress.current) return;
        snapInProgress.current = false;

        setCurrentIndex((prev) => {
            if (snapTarget === -1)
                return (((prev + 1) % slideCount) + slideCount) % slideCount;
            if (snapTarget === 1)
                return (((prev - 1) % slideCount) + slideCount) % slideCount;
            return prev;
        });
        dragProgressRef.current = 0;
        setDragProgress(0);
        setSnapTarget(0);
        setIsSnapping(false);
    }, [snapTarget, slideCount]);

    // Safety timeout: if transitionend never fires, unblock after the
    // animation should have completed (plus a small margin).
    React.useEffect(() => {
        if (!isSnapping) return;
        const timeout = setTimeout(finishSnap, MEDIA_DURATION_MS + 100);
        return () => clearTimeout(timeout);
    }, [isSnapping, finishSnap]);

    React.useEffect(() => {
        rootRef.current?.querySelectorAll("video").forEach((v) => {
            v.pause();
            v.preload = "auto";
            v.controls = false;
            v.removeAttribute("controls");
        });

        const video = getActiveVideo();
        if (!video) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- clear stale active-video reference; not a cascading render
            setActiveVideo(null);
            return;
        }
        video.playsInline = true;
        video.loop = true;
        video.controls = false;
        video.removeAttribute("controls");
        video.currentTime = 0;
        video.muted = true;
        setActiveVideo(video);
        video.play().catch(() => {});
    }, [currentIndex]);

    const handleTransitionEnd = (e: React.TransitionEvent) => {
        if ((e.target as HTMLElement).parentElement === e.currentTarget) {
            finishSnap();
        }
    };

    // Crossfade the text panel: render both the outgoing (active) and incoming
    // (partner) slides simultaneously with mirrored opacities, derived from
    // drag/snap state. Predicting the partner from drag direction or snapTarget
    // means we don't need an extra animation pass after finishSnap — by the
    // time currentIndex swaps, the partner is already at opacity 1.
    const dragMagnitude = Math.min(1, Math.abs(dragProgress));
    const getPartnerIndex = (): number | null => {
        if (slideCount < 2) return null;
        if (isSnapping) {
            // Snap-back animates partner opacity to 0 — keep it out of the
            // layout (mobile uses [data-partner] in display rules) so the
            // panel doesn't stay grown to the partner's height during the
            // animation and then jump on finishSnap.
            if (snapTarget === 0) return null;
            return wrap(currentIndex + (snapTarget === -1 ? 1 : -1));
        }
        if (dragProgress < 0) return wrap(currentIndex + 1);
        if (dragProgress > 0) return wrap(currentIndex - 1);
        return null;
    };
    const partnerIndex = getPartnerIndex();
    const activeOpacity = isSnapping
        ? snapTarget === 0
            ? 1
            : 0
        : 1 - dragMagnitude;
    const partnerOpacity = isSnapping
        ? snapTarget === 0
            ? 0
            : 1
        : dragMagnitude;

    const renderMediaStack = (
        offset: number,
        onTransitionEnd?: (e: React.TransitionEvent) => void,
    ) => (
        <div className={styles.mediaStack} onTransitionEnd={onTransitionEnd}>
            {fullSlides.map((slide, i) => (
                <div
                    key={i}
                    className={styles.stackSlide}
                    style={getStackSlideStyle(i, offset)}
                    data-active-slide={i === currentIndex + 2 ? "" : undefined}
                >
                    {slide.media && (
                        <MediaChooserBlock
                            value={slide.media}
                            attrs={{ className: styles.trackImage }}
                        />
                    )}
                </div>
            ))}
        </div>
    );

    if (slideCount === 0) return null;

    return (
        <section
            ref={rootRef}
            className={concatClassNames([
                styles.root,
                getThemeClass(moduleTheme),
            ])}
            style={{
                "--carousel-duration": `${MEDIA_DURATION_MS}ms`,
            }}
            role="group"
            aria-roledescription={t`carousel`}
            aria-label={ariaLabel}
        >
            {showGraphic && (
                <div className={styles.graphic} aria-hidden="true">
                    <GlobeSvg />
                </div>
            )}
            <div className={styles.leftCard} aria-hidden="true">
                <div className={styles.mediaViewport}>
                    {renderMediaStack(-1)}
                </div>
            </div>

            <div
                className={styles.centerCard}
                role="group"
                aria-roledescription={t`slide`}
                aria-label={t`Slide ${currentIndex + 1} of ${slideCount}`}
            >
                <div
                    className={concatClassNames([
                        styles.textPanel,
                        getThemeClass(cardTheme),
                        textPanelClassName,
                    ])}
                >
                    {slides.map((slide, i) => {
                        const isActive = i === currentIndex;
                        const isPartner = i === partnerIndex;
                        const inlineOpacity = isActive
                            ? activeOpacity
                            : isPartner
                              ? partnerOpacity
                              : undefined;
                        return (
                            <div
                                key={i}
                                className={styles.textSlide}
                                data-active={isActive ? "" : undefined}
                                data-partner={isPartner ? "" : undefined}
                                style={
                                    inlineOpacity !== undefined
                                        ? { opacity: inlineOpacity }
                                        : undefined
                                }
                                // inert removes the subtree from focus order AND
                                // hides it from assistive tech, so links/buttons
                                // inside non-active slides aren't keyboard-reachable
                                // even though they're mounted for stable height.
                                // aria-hidden kept as a defensive fallback for the
                                // small set of browsers that don't yet support inert.
                                inert={!isActive}
                                aria-hidden={!isActive}
                            >
                                {renderTextPanel(slide, i)}
                            </div>
                        );
                    })}
                </div>
                <div
                    ref={centerMediaRef}
                    className={styles.mediaViewport}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onLostPointerCapture={handlePointerUp}
                >
                    {/* eslint-disable-next-line react-hooks/refs -- handleTransitionEnd is only invoked as a DOM event handler, never during render */}
                    {renderMediaStack(0, handleTransitionEnd)}
                    <VideoPlayerControls
                        video={activeVideo}
                        wrapperAttrs={{
                            onPointerDown: (e) => e.stopPropagation(),
                        }}
                    />
                </div>
            </div>

            <div className={styles.rightCard} aria-hidden="true">
                <div className={styles.mediaViewport}>
                    {renderMediaStack(1)}
                </div>
            </div>

            <div className={styles.controls}>
                <IconButton
                    icon="caret"
                    label={t`Previous slide`}
                    onPress={goPrev}
                />
                <IconButton
                    icon="caret"
                    label={t`Next slide`}
                    onPress={goNext}
                />
            </div>

            <div
                className={styles.dotsWrapper}
                role="group"
                aria-label={t`Choose slide to display`}
            >
                {slides.map((_, i) => (
                    <Clickable
                        key={i}
                        className={styles.dot}
                        onPress={() => goTo(i)}
                        data-active={i === currentIndex ? "" : undefined}
                    >
                        <VisuallyHidden>
                            {t`Slide ${i + 1} of ${slideCount}`}
                        </VisuallyHidden>
                    </Clickable>
                ))}
            </div>

            <VisuallyHidden aria-live="polite" aria-atomic="true">
                {t`Showing slide ${currentIndex + 1} of ${slideCount}`}
            </VisuallyHidden>
        </section>
    );
};
